import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import SEO from './components/SEO';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import ProblemSection from './components/ProblemSection';
import FeatureSection from './components/FeatureSection';
import ValueStack from './components/ValueStack';
import PricingSection from './components/PricingSection';
import GuaranteeSection from './components/GuaranteeSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import SignupModal from './components/SignupModal';
import LeadMagnetHero from './components/LeadMagnetHero';
import { AuthProvider } from './contexts/AuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { useFoundingCount } from './hooks/useFoundingCount';

// Lazy-load admin (code-split from landing page)
const AdminLayout = lazy(() => import('./admin/layout/AdminLayout'));
const ProtectedRoute = lazy(() => import('./admin/components/ProtectedRoute'));
const LoginPage = lazy(() => import('./admin/pages/LoginPage'));
const DashboardPage = lazy(() => import('./admin/pages/DashboardPage'));
const CustomersPage = lazy(() => import('./admin/pages/CustomersPage'));
const SupportQueuePage = lazy(() => import('./admin/pages/SupportQueuePage'));
const SupportHistoryPage = lazy(() => import('./admin/pages/SupportHistoryPage'));
const EquipmentPage = lazy(() => import('./admin/pages/EquipmentPage'));
const BroadcastEmailPage = lazy(() => import('./admin/pages/BroadcastEmailPage'));
const OutreachPage = lazy(() => import('./admin/pages/OutreachPage'));
const OutreachOAuthSetupPage = lazy(() => import('./admin/pages/OutreachOAuthSetupPage'));
const OutreachColdCallScriptPage = lazy(() => import('./admin/pages/OutreachColdCallScriptPage'));
const AuditLogPage = lazy(() => import('./admin/pages/AuditLogPage'));
const SettingsPage = lazy(() => import('./admin/pages/SettingsPage'));
const PMCalendarPage = lazy(() => import('./admin/pages/PMCalendarPage'));
const OnsiteInventoryPage = lazy(() => import('./admin/pages/OnsiteInventoryPage'));
const PlaybookPage = lazy(() => import('./admin/pages/PlaybookPage'));
const RepairCalcPage = lazy(() => import('./admin/pages/RepairCalcPage'));
const ScorecardPage = lazy(() => import('./admin/pages/ScorecardPage'));
const EquipmentDetailPage = lazy(() => import('./admin/pages/EquipmentDetailPage'));
const SupportSubmitPage = lazy(() => import('./pages/SupportSubmitPage'));
const FreeSupportPage = lazy(() => import('./pages/FreeSupportPage'));
const FreePMGuidePage = lazy(() => import('./pages/FreePMGuidePage'));
const FreePlaybookPage = lazy(() => import('./pages/FreePlaybookPage'));
const FreeCalculatorPage = lazy(() => import('./pages/FreeCalculatorPage'));
const AdminSupportSubmitPage = lazy(() => import('./admin/pages/AdminSupportSubmitPage'));

// Customer portal
const CustomerLayout = lazy(() => import('./customer/layout/CustomerLayout'));
const CustomerProtectedRoute = lazy(() => import('./customer/components/CustomerProtectedRoute'));
const CustomerLoginPage = lazy(() => import('./customer/pages/CustomerLoginPage'));
const CustomerDashboardPage = lazy(() => import('./customer/pages/CustomerDashboardPage'));
const CustomerSupportPage = lazy(() => import('./customer/pages/CustomerSupportPage'));
const CustomerSupportSubmitPage = lazy(() => import('./customer/pages/CustomerSupportSubmitPage'));
const CustomerSettingsPage = lazy(() => import('./customer/pages/CustomerSettingsPage'));
const CustomerEquipmentPage = lazy(() => import('./customer/pages/CustomerEquipmentPage'));
const CustomerPMBoardPage = lazy(() => import('./customer/pages/CustomerPMBoardPage'));
const CustomerOnsiteInventoryPage = lazy(() => import('./customer/pages/CustomerOnsiteInventoryPage'));
const CustomerRepairCalcPage = lazy(() => import('./customer/pages/CustomerRepairCalcPage'));
const CustomerPlaybookPage = lazy(() => import('./customer/pages/CustomerPlaybookPage'));
const CustomerEquipmentDetailPage = lazy(() => import('./customer/pages/CustomerEquipmentDetailPage'));
const CustomerScorecardPage = lazy(() => import('./customer/pages/CustomerScorecardPage'));
const CustomerSetupWizardPage = lazy(() => import('./customer/pages/CustomerSetupWizardPage'));
const CustomerReferralPage = lazy(() => import('./customer/pages/CustomerReferralPage'));

function AdminLoader() {
  return (
    <div className="min-h-screen bg-[#161b26] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
    </div>
  );
}

function LandingPage() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const spotsTaken = useFoundingCount();

  // Capture referral code + fire outreach-click beacon from URL
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const ref = q.get('ref');
    if (ref) localStorage.setItem('athnx_ref', ref);
    const o = q.get('o');
    if (o && /^[ABCD]\d{1,3}$/i.test(o)) {
      const w = q.get('w') === '2' ? '2' : '1';
      // sendBeacon is fire-and-forget and survives navigation
      const url = `/api/public/outreach-click?o=${encodeURIComponent(o)}&w=${w}`;
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(url);
      } else {
        fetch(url, { method: 'GET', keepalive: true }).catch(() => { /* ignore */ });
      }
    }
  }, []);

  const handleSelectTier = (tierId: string, interval: 'monthly' | 'annual') => {
    if (tierId === 'iron') {
      setBillingInterval(interval);
      setSignupOpen(true);
    }
  };

  return (
    <>
      <SEO />
      <Navbar onOpenSignup={() => setSignupOpen(true)} />
      <main>
        <HeroSection onOpenSignup={() => setSignupOpen(true)} spotsTaken={spotsTaken} />
        <LeadMagnetHero />
        <ProblemSection />
        <FeatureSection />
        <ValueStack />
        <PricingSection onSelectTier={handleSelectTier} />
        <GuaranteeSection />
        <CTASection onOpenSignup={() => setSignupOpen(true)} spotsTaken={spotsTaken} />
      </main>
      <Footer />
      <SignupModal isOpen={signupOpen} onClose={() => setSignupOpen(false)} interval={billingInterval} />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/support" element={<Suspense fallback={<AdminLoader />}><SupportSubmitPage /></Suspense>} />
          <Route path="/free-support" element={<Suspense fallback={<AdminLoader />}><FreeSupportPage /></Suspense>} />
          <Route path="/free-pm-guide" element={<Suspense fallback={<AdminLoader />}><FreePMGuidePage /></Suspense>} />
          <Route path="/free-playbook" element={<Suspense fallback={<AdminLoader />}><FreePlaybookPage /></Suspense>} />
          <Route path="/free-calculator" element={<Suspense fallback={<AdminLoader />}><FreeCalculatorPage /></Suspense>} />
          <Route path="/admin/login" element={<Suspense fallback={<AdminLoader />}><LoginPage /></Suspense>} />
          <Route
            path="/admin"
            element={
              <Suspense fallback={<AdminLoader />}>
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              </Suspense>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="support" element={<SupportQueuePage />} />
            <Route path="support/submit" element={<AdminSupportSubmitPage />} />
            <Route path="support/history" element={<SupportHistoryPage />} />
            <Route path="equipment" element={<EquipmentPage />} />
            <Route path="equipment/:id" element={<EquipmentDetailPage />} />
            <Route path="pm-calendar" element={<PMCalendarPage />} />
            <Route path="onsite-inventory" element={<OnsiteInventoryPage />} />
            <Route path="repair-calc" element={<RepairCalcPage />} />
            <Route path="playbook" element={<PlaybookPage />} />
            <Route path="scorecard" element={<ScorecardPage />} />
            <Route path="broadcast" element={<BroadcastEmailPage />} />
            <Route path="outreach" element={<OutreachPage />} />
            <Route path="outreach/oauth-setup" element={<OutreachOAuthSetupPage />} />
            <Route path="outreach/cold-call-script" element={<OutreachColdCallScriptPage />} />
            <Route path="audit-log" element={<AuditLogPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Customer Portal */}
          <Route path="/portal/setup" element={<Suspense fallback={<AdminLoader />}><CustomerAuthProvider><CustomerSetupWizardPage /></CustomerAuthProvider></Suspense>} />
          <Route path="/portal/login" element={<Suspense fallback={<AdminLoader />}><CustomerAuthProvider><CustomerLoginPage /></CustomerAuthProvider></Suspense>} />
          <Route
            path="/portal"
            element={
              <Suspense fallback={<AdminLoader />}>
                <CustomerAuthProvider>
                  <CustomerProtectedRoute>
                    <CustomerLayout />
                  </CustomerProtectedRoute>
                </CustomerAuthProvider>
              </Suspense>
            }
          >
            <Route index element={<CustomerDashboardPage />} />
            <Route path="support" element={<CustomerSupportPage />} />
            <Route path="support/submit" element={<CustomerSupportSubmitPage />} />
            <Route path="equipment" element={<CustomerEquipmentPage />} />
            <Route path="equipment/:id" element={<CustomerEquipmentDetailPage />} />
            <Route path="pm-board" element={<CustomerPMBoardPage />} />
            <Route path="onsite-inventory" element={<CustomerOnsiteInventoryPage />} />
            <Route path="repair-calc" element={<CustomerRepairCalcPage />} />
            <Route path="playbook" element={<CustomerPlaybookPage />} />
            <Route path="scorecard" element={<CustomerScorecardPage />} />
            <Route path="referrals" element={<CustomerReferralPage />} />
            <Route path="settings" element={<CustomerSettingsPage />} />
          </Route>
        </Routes>
    </AuthProvider>
  );
}
